import { useParams } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  OrderDetailsProvider,
  useOrderDetails,
} from "../../context/OrderDetailContext";
import { CustomerCard } from "../../components/cards/CustomerCard";
import { PaymentDetailsCard } from "../../components/cards/PaymentDetailsCard";
import { ShippingCard } from "../../components/cards/ShippingCard";
import { getOrderDetails, retryShopifyOrder } from "../../services/orders/orderAxios";
import { ProductDetail } from "../../components/cards/ProductDetail";
import { TopNavBarComponent } from "../../components/navbar/TopNavBarComponent";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { IntegrationCardInfo } from "../../components/cards/IntegrationCardInfo";
import { DropiState, ShopifyState } from "../SalesPage/SalesPageInterface";
import { dictionary } from "../../utils/dictionary";
import { CustomerFieldsCard } from "../../components/cards/CustomerFieldsCard";
import { handleApi } from "../../utils/handleApi";
import { ConfirmationInfo, getConfirmationStatuses } from "../../services/whatsapp/whatsappService";
import { decodeTokenService } from "../../services/decodeTokenService";
import { DropiRetryCard } from "../../components/cards/DropiRetryCard";
import { RefreshCw } from "lucide-react";
import { toast } from "react-hot-toast";

const ShopifyRetryCard = ({
  onRetryComplete,
}: {
  onRetryComplete: () => void;
}) => {
  const { orderDetails } = useOrderDetails();
  const [isRetrying, setIsRetrying] = useState(false);
  const state = orderDetails?.shopify_integration_state;

  if (!orderDetails || !state || state === ShopifyState.SUCCESS) return null;

  const handleRetryShopify = async () => {
    if (isRetrying || !orderDetails?.id) return;
    setIsRetrying(true);
    const { res, error } = await handleApi(retryShopifyOrder(orderDetails.id), false);
    setIsRetrying(false);

    if (error) {
      toast.error("No se pudo reintentar la sincronización con Shopify.");
      return;
    }

    if (res?.accepted) {
      toast.success("Reintento Shopify encolado.");
    } else if (res?.success) {
      toast.success("Orden sincronizada con Shopify.");
    } else {
      toast("Reintento Shopify ejecutado. Revisa el estado actualizado.");
    }
    onRetryComplete();
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div>
        <h2 className="text-lg font-semibold">Integración Shopify</h2>
        <p className="mt-2 text-sm text-red-600">
          {orderDetails.shopify_integration_error ||
            "La orden no se sincronizó con Shopify. Puedes reintentar la sincronización manualmente."}
        </p>
      </div>
      <button
        type="button"
        onClick={handleRetryShopify}
        disabled={isRetrying}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#4368E5] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#3154c9] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <RefreshCw className={`h-4 w-4 ${isRetrying ? "animate-spin" : ""}`} />
        {isRetrying ? "Reintentando Shopify" : "Reintentar sincronización"}
      </button>
    </div>
  );
};

const OrderDetailsContent = () => {
  const { language } = useAuth();
  const {
    products,
    customer,
    paymentDetails,
    shippingAddress,
    orderDetails,
    loading,
    error,
    setOrderDetailsData,
    setLoadingState,
    setErrorState,
  } = useOrderDetails();

  const { hash_id } = useParams();
  const dataLoadedRef = useRef(false);
  const [errorOrderDetails, setErrorOrderDetails] = useState<string>();
  const [confirmationInfo, setConfirmationInfo] = useState<ConfirmationInfo | null>(null);

  const customFields = customer?.custom_fields ?? [];
  const normalizeKey = (k: unknown) => String(k ?? "").trim().toLowerCase();
  const zipCodeField = customFields.find((f) => normalizeKey(f?.key) === "zip code");
  const zipCode = zipCodeField?.value ? String(zipCodeField.value) : undefined;
  const customFieldsWithoutZip = customFields.filter((f) => normalizeKey(f?.key) !== "zip code");

  const fetchOrderDetails = useCallback(async () => {
    if (!hash_id) return;
    setLoadingState(true);
    setErrorOrderDetails(undefined);
    const { res, error } = await handleApi(getOrderDetails(hash_id), false);

    if (error) {
      setErrorState("Failed to load order details");
      setLoadingState(false);
      return;
    }

    if (res) {
      if (res.order?.dropi_integration_error) {
        setErrorOrderDetails(res.dropi_integration_error || res.order.dropi_integration_error);
      }
      setOrderDetailsData(res);
      setLoadingState(false);
      dataLoadedRef.current = true;
    }
  }, [hash_id, setErrorState, setLoadingState, setOrderDetailsData]);

  useEffect(() => {
    if (dataLoadedRef.current || !hash_id) {
      if (!hash_id) {
        setErrorState("No order ID provided");
      }
      return;
    }
    fetchOrderDetails();
  }, [hash_id, fetchOrderDetails, setErrorState]);

  useEffect(() => {
    if (!orderDetails?.id) return;
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (!ownerId) return;
    const fetchConfirmation = async () => {
      const { res, error: err } = await handleApi(getConfirmationStatuses(ownerId, [orderDetails.id]), false);
      if (!err && res) {
        const info = res[String(orderDetails.id)];
        if (info) setConfirmationInfo(info);
      }
    };
    fetchConfirmation();
  }, [orderDetails?.id]);

  const handleAddressUpdated = () => {
    dataLoadedRef.current = false;
    fetchOrderDetails();
  };

  const isDropiRetryable =
    orderDetails?.dropi_state === DropiState.ERROR ||
    orderDetails?.dropi_state === DropiState.PENDING_SYNC;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <TopNavBarComponent />
      <div className="flex flex-row md:flex-row">
        {/* Main content column - takes up 70% of the space */}
        <div className="m-6 md:w-[70%]">
          {orderDetails && (
            <ProductDetail order={orderDetails} products={products} />
          )}
        </div>

        {/* Sidebar column - takes up 30% of the space */}
        <div className="m-6 md:w-[30%] space-y-4 flex flex-col gap-4">
          {customer && <CustomerCard customer={customer} />}
          {paymentDetails && (
            <PaymentDetailsCard paymentDetails={paymentDetails} />
          )}
          {shippingAddress && (
            <ShippingCard
              shippingAddress={shippingAddress}
              zipCode={zipCode}
              confirmationId={confirmationInfo?.confirmation_id}
              aiEnabled={confirmationInfo?.ai_enabled}
              onAddressUpdated={handleAddressUpdated}
            />
          )}
          {customFieldsWithoutZip.length > 0 && (
            <CustomerFieldsCard customFields={customFieldsWithoutZip} />
          )}

          {orderDetails?.dropi_state === DropiState.SUCCESS &&
            orderDetails?.dropi_url && (
              <IntegrationCardInfo
                title={dictionary["integration-dropi"][language]}
                description={
                  dictionary["integration-dropi-description-success"][language]
                }
                buttonText={
                  dictionary["integration-dropi-button-text"][language]
                }
                buttonLink={orderDetails?.dropi_url}
              />
            )}
          {isDropiRetryable && orderDetails && customer && (
            <DropiRetryCard
              orderId={orderDetails.id}
              dropiState={orderDetails.dropi_state}
              customer={customer}
              error={errorOrderDetails}
              onSynced={handleAddressUpdated}
            />
          )}

          {orderDetails?.shopify_integration_state === ShopifyState.SUCCESS && (
              <IntegrationCardInfo
                title={dictionary["integration-shopify"][language]}
                description={
                  dictionary["integration-shopify-description-success"][language]
                }
                // buttonText={
                //   dictionary["integration-shopify-button-text"][language]
                // }
                // buttonLink={orderDetails?.dropi_url}
              />
            )}
          <ShopifyRetryCard onRetryComplete={fetchOrderDetails} />
        </div>
      </div>
    </div>
  );
};

export const OrderDetailsPage = () => {
  const { hash_id } = useParams();

  return (
    <OrderDetailsProvider hashId={hash_id}>
      <OrderDetailsContent />
    </OrderDetailsProvider>
  );
};
